import camelize from 'camelize';
import 'regenerator-runtime/runtime.js';
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';

import { GoogleTagManager as GTM } from '@vpr/tracking';

import { inline } from '@vimeo/live-core/utils/general/lambda';
import { customRecorder, gtmRecorder, LoadTimingTracker } from '@vimeo/load-timing';
import { useBroadcastChannel } from '@vimeo/use-broadcast-channel';
import { useViewer } from '@vimeo/viewer';

import { withViewer } from '../../libs/wrapper/withViewer';
import { LeadCaptureForm, LeadCaptureFormWrapper } from '../lead-capture-form';
import { useEntityLiveStatus, useRefreshConfirmationPage } from '../lead-capture-form/hooks';
import { useRegistrantStatus } from '../lead-capture-form/hooks';
import { useLeadFormPrefillParentListener } from '../lead-capture-form/hooks/useLeadFormPrefill';
import { useGlobalStore } from '../lead-capture-form/state/globalStore';
import { ENTITY_TYPE, PREVIEW_TYPE } from '../lead-capture-form/utils/constants';
import {
  deleteCookie,
  getCookie,
  getEntityCookiePassword,
  getEventAppearanceURI,
  setCookie,
  getEntityIdFromApiEntityId,
  getPasswordFromQueryParam,
  getHashedPasswordFromQueryParam,
} from '../lead-capture-form/utils/helper';
import { EVENT_STATUS } from '../lead-capture-form/utils/types';
import AlbumErrorState from './components/AlbumErrorState';
import RLEErrorState from './components/RLEErrorState';
import {
  BROADCAST_CHANNEL_MESSAGE_PAYLOAD_TYPE,
  BROADCAST_CHANNEL_MESSAGE_TYPE,
  BROADCAST_CHANNEL_NAME,
} from './constants/embed_settings';
import ErrorContainer from './containers/ErrorContainer';
import { lazyLoad } from './helper/lazyLoader';
import { Player } from './Player';

const getPlayerWithInteractivityTools = () => {
  return lazyLoad(
    () => import('./PlayerWithInteractivityTools'),
    () => <div />,
  );
};

const EmbedPlaylist = ({ hasInteractivityTools = false }) => {
  const [data, setData] = useState(null);
  const viewer = useViewer();

  const askDataUnsubscriberRef = useRef(null);
  const newDataUnsubscriberRef = useRef(null);

  const { refresh: refreshConfirmationPageData } = useRefreshConfirmationPage();

  const clips = data?.clips || null;
  const error = data?.error;
  const errorBackground = data?.errorBackground;
  const isOwner = data?.isOwner;
  const liveEvent = data?.liveEvent || null;
  const playlistData = data?.playlistData || {};
  const isRLE = playlistData.type === 'RLE';
  const PlayerComponent = hasInteractivityTools
    ? /* eslint-disable-next-line react-hooks/rules-of-hooks */
      useMemo(getPlayerWithInteractivityTools, [])
    : Player;

  useEffect(() => {
    if (!data) {
      setData(camelize(window.dataForPlayer));
    }
  }, [data]);

  useEffect(() => {
    if (!data) {
      return;
    }

    const timingEndpoint = `${
      process.env.NODE_ENV === 'production' && !window.location.host.endsWith('.ci.vimeows.com')
        ? 'https://fresnel-events.vimeocdn.com'
        : 'https://fresnel-event-staging.vimeows.com'
    }/timing`;

    const trackers = [
      customRecorder({
        token: viewer?.xsrft,
        endpoint: timingEndpoint,
        headers: {
          'Content-Type': 'application/json',
          'User-Agent': navigator.userAgent,
          Origin: location.origin,
          Referer: document.referrer,
        },
      }),
    ];

    if ('_gtm' in window) {
      GTM.init(window._gtm);
      trackers.push(gtmRecorder);
    }

    const loadTimingTracker = new LoadTimingTracker(trackers);
    // Tracking:
    const trackingLabel = `${playlistData.type.toLowerCase()}_${error ? 'error' : 'app_load'}`;
    loadTimingTracker.recordDOMContentLoaded('embed_playlist', trackingLabel);
    loadTimingTracker.recordDOMContentLoadEventDuration('embed_playlist', trackingLabel);
    loadTimingTracker.recordLoaded('embed_playlist', trackingLabel);
    loadTimingTracker.recordLoadEventDuration('embed_playlist', trackingLabel);
    loadTimingTracker.recordPageRenderDuration('embed_playlist', trackingLabel);
  }, [data]);

  const leadCaptureCookie = useGlobalStore((store) => store.cookie);
  const hasNewLeadCaptureFlow = useGlobalStore((store) => store.hasNewLeadCaptureFlow);
  const apiEntityId = useGlobalStore((store) => store.apiEntityId);
  const entityId = useGlobalStore((store) => store.entityId);
  const entityType = useGlobalStore((store) => store.entityType);
  const { setPreviewType } = useGlobalStore((store) => store);

  const status = useEntityLiveStatus(hasNewLeadCaptureFlow || false);
  const registrant = useRegistrantStatus(leadCaptureCookie, status);

  const isRegistrantBlocked =
    (registrant.data.isBlocked && registrant.called) || !registrant.initialDataLoaded;

  const isEmbeddedInAppearancePage =
    isRLE &&
    window.frameElement &&
    window.top.location.pathname === getEventAppearanceURI(playlistData.id);

  /**
   * Scenario hide lead capture
   * 1. If event is ended(ie.. the user clicks on complete event button)
   * 2. If user has registered and event has started (started necessarily does not mean event is live, but means at-least one event went live)
   * 3. If embedded in the event appearance page
   */
  const showLeadCaptureFormForEvent =
    !isEmbeddedInAppearancePage &&
    entityType === ENTITY_TYPE.EVENT &&
    (!(status === EVENT_STATUS.ENDED || (status === EVENT_STATUS.STARTED && leadCaptureCookie)) ||
      (isRegistrantBlocked && status !== EVENT_STATUS.ENDED));

  const showLeadCaptureFormForShowcase = entityType === ENTITY_TYPE.SHOWCASE && !leadCaptureCookie;

  const showLeadCaptureForm = showLeadCaptureFormForEvent || showLeadCaptureFormForShowcase;

  useEffect(() => {
    /**
     * In case if registrant is not valid, that means the registrant could have been deleted by owner.
     * So, we clear the registrant uuid from cookie.
     * Then it will prompt to register again.
     */
    const isRegistrantDeletedOrInvalid =
      !registrant.loading && registrant.called && !registrant.data.isValidRegistrant;
    if (isRegistrantDeletedOrInvalid) {
      useGlobalStore.setState({ cookie: undefined });
      deleteCookie(apiEntityId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registrant, apiEntityId]);

  useEffect(() => {
    /** If cookie is set, save the cookie value to zustand store as well */
    const cookie = getCookie(apiEntityId);
    useGlobalStore.setState({ cookie: cookie && cookie.length > 0 ? cookie : undefined });
  }, [apiEntityId]);

  useLeadFormPrefillParentListener(entityId, entityType, (message) => {
    /** Respond only if request is coming from a child entity */
    const childEntityId = getEntityIdFromApiEntityId(message?.entityId || '');
    switch (entityType) {
      case ENTITY_TYPE.EVENT:
      case ENTITY_TYPE.SHOWCASE:
        return clips?.some((clip) => clip.id == childEntityId);
      default:
        return false;
    }
  });

  const { isChannelActive, sendChannelMessage, subscribe, closeChannel } = useBroadcastChannel({
    name: BROADCAST_CHANNEL_NAME,
  });

  const onUnsubscribeFromAskData = useCallback(() => {
    if (askDataUnsubscriberRef.current) {
      askDataUnsubscriberRef.current();
      askDataUnsubscriberRef.current = null;
    }
  }, []);

  const onUnsubscribeFromNewData = useCallback(() => {
    if (newDataUnsubscriberRef.current) {
      newDataUnsubscriberRef.current();
      newDataUnsubscriberRef.current = null;
    }
  }, [entityType]);

  const onSubscribeToAskData = useCallback(() => {
    if (askDataUnsubscriberRef.current) return;

    askDataUnsubscriberRef.current = subscribe((message) => {
      if (
        message.type !== BROADCAST_CHANNEL_MESSAGE_TYPE.ASK_DATA ||
        String(message?.payload?.sessionId) !== String(entityId)
      ) {
        return;
      }

      const payloadType = message?.payload?.type;

      const value = inline(() => {
        switch (payloadType) {
          case BROADCAST_CHANNEL_MESSAGE_PAYLOAD_TYPE.PASSWORD:
            return getPasswordFromQueryParam();

          case BROADCAST_CHANNEL_MESSAGE_PAYLOAD_TYPE.HASHED_PASSWORD:
            return getEntityCookiePassword(apiEntityId, entityType);

          case BROADCAST_CHANNEL_MESSAGE_PAYLOAD_TYPE.REGISTRANT_UUID:
            return leadCaptureCookie;

          default:
            return undefined;
        }
      });

      if (!value) return;

      sendChannelMessage({
        type: BROADCAST_CHANNEL_MESSAGE_TYPE.NEW_DATA,
        payload: {
          sessionId: String(entityId),
          type: payloadType,
          value,
        },
      });
    });
  }, [apiEntityId, entityId, entityType, subscribe, sendChannelMessage, leadCaptureCookie]);

  const onSubscribeToNewData = useCallback(() => {
    if (!newDataUnsubscriberRef.current) {
      newDataUnsubscriberRef.current = subscribe(async (message) => {
        if (
          message.type !== BROADCAST_CHANNEL_MESSAGE_TYPE.NEW_DATA ||
          String(message?.payload?.sessionId) !== String(entityId)
        ) {
          return;
        }

        if (message.payload.type === BROADCAST_CHANNEL_MESSAGE_PAYLOAD_TYPE.REGISTRANT_UUID) {
          const leadId = message.payload.value;
          if (leadId) {
            const confirmationPageData = await refreshConfirmationPageData(leadId);

            if (confirmationPageData) {
              setCookie(apiEntityId, leadId);
              setPreviewType(PREVIEW_TYPE.CONFIRMATION);
              /**
               * In case of multi step form,
               *
               * If registration is successful, reset the current field index to point to the first field,
               * so in case if registrant was deleted by admin while confirmation page is kept open,
               * the registrant will see the registration page again starting from the first field.
               * Otherwise registrant will see the last field.
               */
              useGlobalStore.setState({ cookie: leadId, currentSelectedFieldIndex: 0 });
            }
          }
        }
      });
    }
  }, [apiEntityId, subscribe, entityId, refreshConfirmationPageData, setPreviewType]);

  useEffect(() => {
    if (!isChannelActive) {
      return;
    }

    if (error) {
      /**
       * password need
       * unsubscribe from all messages
       */
      onUnsubscribeFromAskData();
      onUnsubscribeFromNewData();
    } else {
      /**
       * no password needed
       * subscribe to ask_data to share password and/or registrant uuid only if they present
       */
      onSubscribeToAskData();

      if (!leadCaptureCookie) {
        /**
         * registrant uuid needed
         * subscribe to new_data with registrant uuid
         */
        onSubscribeToNewData();
      } else {
        /**
         * no registrant uuid needed
         * unsubscribe from new_data
         */
        onUnsubscribeFromNewData();
      }
    }
  }, [
    isChannelActive,
    leadCaptureCookie,
    error,
    onUnsubscribeFromAskData,
    onUnsubscribeFromNewData,
    onSubscribeToNewData,
    onSubscribeToAskData,
    entityType,
  ]);

  useEffect(() => {
    return () => {
      onUnsubscribeFromAskData();
      onUnsubscribeFromNewData();
      closeChannel();
    };
  }, [onUnsubscribeFromAskData, onUnsubscribeFromNewData, closeChannel]);

  // RLE Error States:
  if (isRLE && (error || !clips)) {
    return (
      <ErrorContainer
        playlistId={playlistData.id}
        type={playlistData.type}
        apiEntityId={apiEntityId}
        render={({ handleChange, handleSubmit, handleLogin, invalidPassword, password }) => (
          <RLEErrorState
            error={error}
            errorBackground={errorBackground}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            handleLogin={handleLogin}
            invalidPassword={invalidPassword}
            password={password}
            sso={playlistData.sso}
            color={playlistData.color}
            isOwner={isOwner}
            playlistId={playlistData.id}
          />
        )}
      />
    );
  }

  // Album Error States:
  if (error || clips?.length === 0) {
    return (
      <ErrorContainer
        playlistId={playlistData.id}
        type={playlistData.type}
        apiEntityId={apiEntityId}
        render={({ handleChange, handleLogin, handleSubmit, invalidPassword, password }) => (
          <AlbumErrorState
            error={error}
            errorBackground={errorBackground}
            handleChange={handleChange}
            handleLogin={handleLogin}
            handleSubmit={handleSubmit}
            invalidPassword={invalidPassword}
            password={password}
            title={playlistData.title}
            customLogo={playlistData.customLogo}
            color={playlistData.color}
            sso={playlistData.sso}
          />
        )}
      />
    );
  }

  if (showLeadCaptureForm && hasNewLeadCaptureFlow) {
    return (
      <LeadCaptureFormWrapper>
        <LeadCaptureForm
          /**
           * If user is registered, then show confirmation page. Presence of cookie means user registered.
           */
          previewType={leadCaptureCookie ? PREVIEW_TYPE.CONFIRMATION : PREVIEW_TYPE.FORM}
        />
      </LeadCaptureFormWrapper>
    );
  }

  return (
    <PlayerComponent
      clips={clips}
      isRLE={isRLE}
      hashedPassword={getHashedPasswordFromQueryParam()}
      liveEvent={liveEvent}
      playlistData={playlistData}
      eventStatus={status}
      isEmbeddedInAppearancePage={isEmbeddedInAppearancePage}
    />
  );
};

export default withViewer(EmbedPlaylist);
